/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Accordion,
    Box,
    Text,
    Button,
    useDisclosure,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {ChevronRightIcon} from '../../../components/icons'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'
import {getPriceData} from '../../../utils/product-utils'

/* Components */
import ShippingPickupOptions from './shipping-pickup-options'
import ShippingPickupModal from '../../../components/shipping-pickup-modal'
import GiftCardContentDrawer from './gift-card-content-drawer'
import {renderLayoutsSwitch} from '../../../components/optimizely/utils/contentRenderer'
import {extractPDPAreaBlocks} from '../utils/extract-pdp-area-blocks'
import {GDIDynamicContentProvider} from '../../../components/optimizely/gdi/utils/gdi-dynamic-content-context'

/* Hooks */
import {useDeliveryOptions} from '../../../integrations/delivery-options/hooks/use-delivery-options'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

const InformationAccordion = ({
    product,
    openFindInStoreModal,
    openLongDescriptionModal,
    optimizelyContent,
    isOptimizelyLoading,
    isGiftPage = false,
    checkBalanceOnOpen,
    pdpCmsContext
}) => {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const {formatMessage} = useIntl()
    const {getTokenWhenReady} = useAccessToken()
    const {data: customer} = useCurrentCustomer()
    const [token, setToken] = useState(null)
    const {deliveryOptionResponse} = useDeliveryOptions({accessToken: token, cache: true})
    const [deliveryOptionsData, setDeliveryOptionsData] = useState({})
    const [cmsDeliverySectionBlock, setCmsDeliverySectionBlock] = useState(null)
    const {
        isOpen: isShippingPickupOpen,
        onOpen: onShippingPickupOpen,
        onClose: onShippingPickupClose
    } = useDisclosure()
    const {
        isOpen: isGiftCardFaqOpen,
        onOpen: onGiftCardFaqOpen,
        onClose: onGiftCardFaqClose
    } = useDisclosure()
    const {
        isOpen: isTermsAndConditionsOpen,
        onOpen: onTermsAndConditionsOpen,
        onClose: onTermsAndConditionsClose
    } = useDisclosure()

    const stretchScale = product?.c_stretchScale
    const productDescription = product?.shortDescription || product?.longDescription
    const step =
        stretchScale != null
            ? stretchScale < 10
                ? 'rigid'
                : stretchScale <= 20
                ? 'comfort'
                : 'stretch'
            : undefined
    const styles = useMultiStyleConfig('InformationAccordion', {step})

    /* GDIPDPPage BuyBox areas — the `pdp` CMS page is shared across all PDPs,
     * and InformationAccordion is only rendered on PDP, so these always render
     * here when populated. `BuyBoxAboveDescriptionContent` renders above the
     * description; `BuyBoxBelowDescriptionContent` is treated as the "Below
     * Drawer Triggers Area" and renders after the shipping/pickup section. */
    const buyBoxAboveDescriptionBlocks = extractPDPAreaBlocks(
        optimizelyContent?.item?.BuyBoxAboveDescriptionContent,
        pdpCmsContext
    )
    const buyBoxBelowDrawerTriggersBlocks = extractPDPAreaBlocks(
        optimizelyContent?.item?.BuyBoxBelowDescriptionContent,
        pdpCmsContext
    )
    const faqDrawerTitleBlock =
        optimizelyContent?.item?.FaqDrawerTitle?.[0]?.ContentLink?.Expanded
    const faqDrawerContentItems = optimizelyContent?.item?.FaqDrawerContent || []
    const hasGiftCardFaqContent = Boolean(faqDrawerTitleBlock) || faqDrawerContentItems.length > 0

    const termsAndConditionsDrawerTitleBlock =
        optimizelyContent?.item?.TermsAndConditionsDrawerTitle?.[0]?.ContentLink?.Expanded
    const termsAndConditionsDrawerContentItems =
        optimizelyContent?.item?.TermsAndConditionsDrawerContent || []
    const hasTermsAndConditionsContent =
        Boolean(termsAndConditionsDrawerTitleBlock) ||
        termsAndConditionsDrawerContentItems.length > 0
    const currentPrice = getPriceData(product).currentPrice
    const loyaltyPointsBase = currentPrice > 0 ? currentPrice : product?.c_loyaltyPointsToEarn
    const pdpLoyaltyPoints =
        loyaltyPointsBase != null
            ? Math.round(Number(loyaltyPointsBase)) * (customer?.c_doublePointsEnabled ? 2 : 1)
            : undefined
    const pdpDynamicContentValue = {
        contentPageType: 'GDIPDPPage',
        pdpProduct: product,
        pdpProductSku: product?.id || product?.productId,
        pdpLoyaltyPoints,
        disableViewportBleed: true
    }

    const fetchToken = async () => {
        try {
            const token = await getTokenWhenReady()
            setToken(token)
        } catch (error) {
            console.error('Error fetching token:', error)
        }
    }

    useEffect(() => {
        fetchToken()
    }, [])

    useEffect(() => {
        if (deliveryOptionResponse?.success) {
            const filteredShippingMethods = Object.fromEntries(
                Object.entries(deliveryOptionResponse?.deliveryOptions || {}).filter(
                    ([, value]) => value !== null
                )
            )
            setDeliveryOptionsData(filteredShippingMethods)
        }
    }, [deliveryOptionResponse])

    useEffect(() => {
        if (isOptimizelyLoading) return
        const pdpPageDeliveryOptions = optimizelyContent?.item?.DeliveryOptions
        const pdpDeliverySection = optimizelyContent?.item?.DeliverySection
        if (Array.isArray(pdpPageDeliveryOptions) && pdpPageDeliveryOptions.length > 0) {
            setCmsDeliverySectionBlock((currentBlock) => ({
                ...currentBlock,
                DeliveryOptions: pdpPageDeliveryOptions,
                GDIPDPPageNotes: pdpDeliverySection?.GDIPDPPageNotes,
                GDIPDPPageTitleStyle: pdpDeliverySection?.GDIPDPPageTitleStyle,
                GDIPDPPagePriceStyle: pdpDeliverySection?.GDIPDPPagePriceStyle,
                DescriptionLine1Style: pdpDeliverySection?.DescriptionLine1Style,
                DescriptionLine2Style: pdpDeliverySection?.DescriptionLine2Style
            }))
        }
    }, [isOptimizelyLoading, optimizelyContent])

    const getVisibleContent = (htmlContent) => {
        if (!htmlContent) return ''

        const ulIndex = htmlContent.indexOf('<ul')
        if (ulIndex === -1) return htmlContent

        const beforeUlContent = htmlContent.substring(0, ulIndex)
        const pTagMatches = beforeUlContent.match(/<p[^>]*>(?![\s<]*(?:<br|<\/p>)).*?<\/p>/gs)

        if (pTagMatches && pTagMatches.length > 0) {
            const lastPTag = pTagMatches[pTagMatches.length - 1]
            if (lastPTag.includes('Features')) {
                return htmlContent.substring(0, beforeUlContent.lastIndexOf(lastPTag))
            }
        }

        return beforeUlContent
    }

    const renderStretchScale = () => {
        return (
            <Box {...styles.stretchScaleOuter}>
                <Box {...styles.stretchScaleBar}>
                    <Box {...styles.stretchScaleIndicator} />
                </Box>
                <Box {...styles.stretchScaleLabels}>
                    <Text {...styles.stretchScaleLabelLeft}>
                        {formatMessage({
                            defaultMessage: 'Rigid',
                            id: 'product_detail.product_stretch_scale.step_rigid'
                        })}
                    </Text>
                    <Text {...styles.stretchScaleLabelCenter}>
                        {formatMessage({
                            defaultMessage: 'Comfort',
                            id: 'product_detail.product_stretch_scale.step_comfort'
                        })}
                    </Text>
                    <Text {...styles.stretchScaleLabelRight}>
                        {formatMessage({
                            defaultMessage: 'Stretch',
                            id: 'product_detail.product_stretch_scale.step_stretch'
                        })}
                    </Text>
                </Box>
            </Box>
        )
    }

    return (
        <Box>
            <Accordion allowMultiple defaultIndex={[0]} {...styles.accordion}>
                {/* CMS area: above the description */}
                {buyBoxAboveDescriptionBlocks.length > 0 && (
                    <GDIDynamicContentProvider value={pdpDynamicContentValue}>
                        <Box>
                            {buyBoxAboveDescriptionBlocks.map((block, index) => (
                                <Box
                                    key={`buybox-above-desc-${
                                        block?.ContentLink?.GuidValue ?? index
                                    }`}
                                >
                                    {renderLayoutsSwitch(block)}
                                </Box>
                            ))}
                        </Box>
                    </GDIDynamicContentProvider>
                )}
                {/* Short description */}
                {!isGiftPage && !!productDescription && (
                    <Box {...styles.descriptionWrapper}>
                        <Text as="h2" {...styles.descriptionHeading}>
                            {formatMessage({
                                defaultMessage: 'Description',
                                id: 'product_detail.product_description'
                            })}
                        </Text>
                        <Box {...styles.descriptionContent}>
                            {!isGiftPage && (
                                <Box
                                    dangerouslySetInnerHTML={{
                                        __html: getVisibleContent(productDescription)
                                    }}
                                />
                            )}
                            {isGiftPage && (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: product?.shortDescription
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                )}

                {/* Stretch scale */}
                {stretchScale && (
                    <Box {...styles.stretchScaleWrapper}>
                        <Text as="h2" {...styles.stretchScaleHeading}>
                            {formatMessage({
                                defaultMessage: 'Stretch scale',
                                id: 'product_detail.product_stretch_scale'
                            })}
                        </Text>
                        <Box {...styles.stretchScalePadding}>{renderStretchScale()}</Box>
                    </Box>
                )}

                {/* Details */}
                {!isGiftPage &&
                    <Button
                        onClick={openLongDescriptionModal}
                        {...styles.navButton}
                        data-testid="pdp-button-product-details"
                    >
                        <Text
                            as="h2"
                            {...styles.navItemText}
                        >
                            {formatMessage({
                                defaultMessage: 'Product Details',
                                id: 'product_detail.accordion.button.product_detail'
                            })}
                            <ChevronRightIcon />
                        </Text>
                    </Button>
                }

                {/* Find In Store */}
                {getSiteEnvVarValue(appConfig, 'FIND_IN_STORE_ENABLED', siteId) === 'true' && !isGiftPage &&
                    <Button
                        onClick={openFindInStoreModal}
                        {...styles.navButton}
                        data-testid="pdp-button-find-in-store"
                    >
                        <Text
                            as="h2"
                            {...styles.navItemText}
                        >
                            {formatMessage({
                                defaultMessage: 'Find In Store',
                                id: 'product_detail.accordion.button.find_in_store'
                            })}
                            <ChevronRightIcon />
                        </Text>
                    </Button>
                }

                {/* Check balance */}
                {getSiteEnvVarValue(appConfig, 'SX_ENABLED', siteId) == 'true' && isGiftPage &&
                    <Button
                        onClick={checkBalanceOnOpen}
                        {...styles.checkBalanceButton}
                        data-testid="pdp-button-check-balance"
                    >
                        <Text
                            as="h2"
                            {...styles.navItemText}
                        >
                            {formatMessage({
                                defaultMessage: 'Check Balance',
                                id: 'product_detail.accordion.button.check_balance'
                            })}
                            <ChevronRightIcon />
                        </Text>
                    </Button>
                }

                {/* Gift Card Sections - FAQ and Terms */}
                {isGiftPage && (
                    <>
                        {/* FAQ Section */}
                        {hasGiftCardFaqContent && (
                            <Button
                                onClick={onGiftCardFaqOpen}
                                {...styles.navButton}
                                data-testid="pdp-button-gift-card-faq"
                            >
                                <Text
                                    as="h2"
                                    {...styles.navItemText}
                                >
                                    {formatMessage({
                                        defaultMessage: 'FAQ',
                                        id: 'product_detail.accordion.button.faq'
                                    })}
                                    <ChevronRightIcon />
                                </Text>
                            </Button>
                        )}

                        {/* Terms and Conditions Section */}
                        {hasTermsAndConditionsContent && (
                            <Button
                                onClick={onTermsAndConditionsOpen}
                                {...styles.navButton}
                                data-testid="pdp-button-terms-and-conditions"
                            >
                                <Text
                                    as="h2"
                                    {...styles.navItemText}
                                >
                                    {formatMessage({
                                        defaultMessage: 'Terms and Conditions',
                                        id: 'product_detail.accordion.button.terms_and_conditions'
                                    })}
                                    <ChevronRightIcon />
                                </Text>
                            </Button>
                        )}
                    </>
                )}

                {/* Shipping and Pickup Options */}
                {!isGiftPage && (
                    <ShippingPickupOptions
                        deliveryOptionsData={deliveryOptionsData}
                        cmsDeliverySectionBlock={cmsDeliverySectionBlock}
                        openShippingPickupModal={onShippingPickupOpen}
                    />
                )}
                {/* CMS area: below the drawer triggers */}
                {buyBoxBelowDrawerTriggersBlocks.length > 0 && (
                    <GDIDynamicContentProvider value={pdpDynamicContentValue}>
                        <Box>
                            {buyBoxBelowDrawerTriggersBlocks.map((block, index) => (
                                <Box
                                    key={`buybox-below-drawers-${
                                        block?.ContentLink?.GuidValue ?? index
                                    }`}
                                >
                                    {renderLayoutsSwitch(block)}
                                </Box>
                            ))}
                        </Box>
                    </GDIDynamicContentProvider>
                )}
            </Accordion>
            <Box {...styles.spacer}></Box>
            <ShippingPickupModal
                isOpen={isShippingPickupOpen}
                onClose={onShippingPickupClose}
                deliveryOptionsData={deliveryOptionsData}
                cmsDeliverySectionBlock={cmsDeliverySectionBlock}
            />

            {/* Gift Card FAQ drawer */}
            <GiftCardContentDrawer
                isOpen={isGiftCardFaqOpen}
                onClose={onGiftCardFaqClose}
                titleBlock={faqDrawerTitleBlock}
                fallbackTitle={formatMessage({
                    defaultMessage: 'Frequently asked questions',
                    id: 'gift_card.faq.title'
                })}
                contentItems={faqDrawerContentItems}
                dynamicContentValue={pdpDynamicContentValue}
                bodyTestId="pdp-gift-card-faq-drawer-body"
            />

            {/* Gift Card Terms and Conditions drawer */}
            <GiftCardContentDrawer
                isOpen={isTermsAndConditionsOpen}
                onClose={onTermsAndConditionsClose}
                titleBlock={termsAndConditionsDrawerTitleBlock}
                fallbackTitle={formatMessage({
                    defaultMessage: 'Terms and Conditions',
                    id: 'product_detail.accordion.button.terms_and_conditions'
                })}
                contentItems={termsAndConditionsDrawerContentItems}
                dynamicContentValue={pdpDynamicContentValue}
                bodyTestId="pdp-terms-and-conditions-drawer-body"
            />
        </Box>
    )
}

InformationAccordion.propTypes = {
    product: PropTypes.object,
    openFindInStoreModal: PropTypes.func,
    openLongDescriptionModal: PropTypes.func,
    optimizelyContent: PropTypes.object,
    isOptimizelyLoading: PropTypes.bool,
    isGiftPage: PropTypes.bool,
    checkBalanceOnOpen: PropTypes.func,
    pdpCmsContext: PropTypes.shape({
        primaryCategoryId: PropTypes.string,
        parentCategoryTree: PropTypes.array
    })
}

export default InformationAccordion
